import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon, PencilIcon, CalendarDaysIcon, CheckIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useConfirm, SelectMenu, DatePicker, TimePicker, Checkbox } from '../ui';
import { TypeaheadChips, Chip } from '../ui';
import { customerAPI, settingsAPI, unwrapList } from '../../services/api';
import { formatWoRangeHouse } from '../../utils/woSchedule';
import DateRangeSelector from './DateRangeSelector';
import { isDeactivatedStatus, deactivatedUserIds, storedIdsWithUserNames, deactivatedChipOptions } from '../../utils/userStatus';

/**
 * CalendarEventModal — Quick-view/edit modal for calendar events (§3.5-2)
 * BIDIT-owned events: editable (date, crew, notes)
 * Imported events: view-only
 */
export function CalendarEventModal({ event, crews = [], onSave, onDelete, onClose, readOnly = false }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const confirm = useConfirm();

  // PT-40.4 (2026-07-27): expose 'View Work Order' link in the footer when
  // the modal is showing a WO-typed event. The event carries `id` (WO id)
  // and `jobId` (parent job) per `CalendarPage.js:1055-1067`. Non-WO events
  // (appointments, tasks, imported) hide the link.
  const isWorkOrder = event?.type === 'work_order' && !!event?.id && !!event?.jobId;
  const handleViewWorkOrder = () => {
    if (!isWorkOrder) return;
    onClose?.();
    // IDI-475: route by job id + WO id, never a first-match scan.
    navigate(`/work-orders/${encodeURIComponent(event.id)}?job=${encodeURIComponent(event.jobId)}`, { state: { jobId: event.jobId } });
  };

  // Iter-18: load customers + users for the same typeaheads the Create sheet uses.
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [users, setUsers] = useState([]);
  // IDI-704: the raw list_users rows, to label a kept deactivated assignee.
  const [rawUsers, setRawUsers] = useState([]);
  useEffect(() => {
    if (!event) return;
    let ignore = false;
    Promise.all([
      customerAPI.getCustomers().catch(() => []),
      settingsAPI.getUsers().catch(() => []),
      // IDI-52 (B3): migrated field-technician roster is the primary assignment
      // source; Cognito users remain as a fallback for net-new tenants.
      settingsAPI.getFieldTechnicians().catch(() => []),
    ]).then(([c, u, techs]) => {
      if (ignore) return;
      // IDI-100: use the shared unwrapper (see CustomerBlock.js for the
      // reported bug this class of code caused). Harmless here today only
      // because this call passes no `limit`.
      setCustomers(unwrapList(c, 'customers', 'CalendarEventModal').list);
      const cognitoUsers = Array.isArray(u) ? u : (Array.isArray(u?.data) ? u.data : (Array.isArray(u?.users) ? u.users : []));
      const fieldTechs = Array.isArray(techs) ? techs : (Array.isArray(techs?.data) ? techs.data : []);
      // Merge field techs first, then any Cognito users not already present.
      // IDI-704 (IDI-681 D1): deactivated logins are not offered.
      const deactivatedIds = deactivatedUserIds(cognitoUsers);
      const byId = new Map();
      fieldTechs.forEach(t => { const id = t.id || t.name; if (id && !deactivatedIds.has(String(id))) byId.set(String(id), { id, name: t.name || t.email || id }); });
      cognitoUsers.forEach(cu => { if (!cu || isDeactivatedStatus(cu.status)) return; const id = cu.id || cu.name; if (id && !byId.has(String(id))) byId.set(String(id), { id, name: cu.name || cu.email || id }); });
      setUsers(Array.from(byId.values()));
      setRawUsers(cognitoUsers);
    });
    return () => { ignore = true; };
  }, [event]);
  const filteredCustomers = customers.filter(c => {
    if (!customerSearch) return true;
    const q = customerSearch.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.company_name || '').toLowerCase().includes(q);
  }).slice(0, 8);

  const isImported = event?.source === 'import' || event?.imported;
  const isReadOnly = isImported;
  // IDI-537: work-order pop-ups are view-only here (no pencil, no Delete);
  // a WO is edited/deleted from its own page and ⋮ menu (IDI-469).
  const isWO = event?.type === 'work_order';
  // IDI-617 D3: readOnly (field techs) hides the pencil and Delete.
  const canEdit = !isReadOnly && !isWO && !readOnly;
  // IDI-609: a stable value so the range picker's sync effect runs only on change.
  const rangeValue = useMemo(
    () => (form.date ? { start: form.date, end: form.endDate || form.date } : null),
    [form.date, form.endDate],
  );
  // IDI-704 (IDI-681 D2): offered users, plus chip-only iconed options for
  // deactivated people already on the event, labelled by their current
  // list_users name. form.assignees is never filtered, so Save is unchanged.
  const assigneeOptions = useMemo(() => {
    const offered = users.map(u => ({ value: u.id || u.name, label: u.name || u.email }));
    const kept = deactivatedChipOptions(
      storedIdsWithUserNames(event?.assignees, rawUsers),
      offered,
      deactivatedUserIds(rawUsers),
    );
    return [...offered, ...kept];
  }, [users, rawUsers, event]);

  useEffect(() => {
    if (event) {
      setForm({
        title: event.title || '',
        date: event.date || (event.time ? event.time.split('T')[0] : ''),
        // IDI-609: the span's last day (one-day events: same as date).
        endDate: (event.endDate && event.date && event.endDate > event.date) ? event.endDate : (event.date || (event.time ? event.time.split('T')[0] : '')),
        startTime: event.startTime || (event.time ? event.time.split('T')[1]?.slice(0, 5) : '08:00'),
        endTime: event.endTime || '17:00',
        crewId: event.crewId || event.crew_id || '',
        notes: event.notes || '',
        location: event.location || '',
        // Iter-18 CAL parity with Create sheet
        customerId: event.customerId || event.customer_id || '',
        customerName: event.customerName || event.customer_name || '',
        assignees: Array.isArray(event.assignees) ? event.assignees : [],
        recurrence: event.recurrence || 'none',
        recurrenceEndDate: event.recurrenceEndDate || event.recurrence_end_date || '',
        allDay: !!event.allDay,
      });
      setEditing(false);
    }
  }, [event]);

  if (!event) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      // IDI-613 (D1): an all-day event saves allDay:true and no times.
      const { startTime, endTime, ...rest } = form;
      // IDI-609 (D4): Edit sends date and endDate (one-day: endDate = date).
      rest.endDate = rest.endDate && rest.endDate > rest.date ? rest.endDate : rest.date;
      await onSave(event.id, form.allDay ? { ...rest, allDay: true } : { ...rest, allDay: false, startTime, endTime });
      setEditing(false);
      onClose();
    } catch (err) {
      console.error('Failed to save event:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Delete event?',
      message: 'Delete this event?',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    await onDelete(event.id);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white w-full max-w-[38rem] mx-4 overflow-hidden" style={{ borderRadius: 'var(--radius-md)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-2">
            {event.type === 'task'
              ? <CheckIcon className="h-5 w-5" style={{ color: 'var(--color-action-primary)' }} />
              : <CalendarDaysIcon className="h-5 w-5" style={{ color: 'var(--color-action-primary)' }} />
            }
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {isWO
                ? `Work order \u00b7 ${event.title || ''}`
                : isImported ? 'Imported Event' : event.type === 'task' ? 'Task' : 'Appointment'}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && !editing && (
              <button
                onClick={() => setEditing(true)}
                className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500"
                aria-label="Edit event"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-500">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {editing ? (
            <>
              <Input
                label="Title"
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
              />
              <div className="grid grid-cols-2 gap-3">
                {/* IDI-609 (D4): Create's Dates range picker replaces "Date". */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dates</label>
                  <DateRangeSelector
                    value={rangeValue}
                    onChange={({ start, end }) => setForm(f => ({ ...f, date: start, endDate: end || start }))}
                    placeholder="Select date range"
                  />
                </div>
                {form.allDay ? <div /> : (
                <div className="grid grid-cols-2 gap-2">
                  <TimePicker
                    label="Start"
                    value={form.startTime}
                    onChange={(v) => setForm(f => ({ ...f, startTime: v }))}
                  />
                  <TimePicker
                    label="End"
                    value={form.endTime}
                    onChange={(v) => setForm(f => ({ ...f, endTime: v }))}
                  />
                </div>
                )}
              </div>
              <Checkbox
                checked={!!form.allDay}
                onChange={(checked) => setForm(f => ({ ...f, allDay: checked }))}
                label="All day"
              />
              {/* Recurrence — positioned under time pickers */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recurrence</label>
                <SelectMenu
                  width="full"
                  value={form.recurrence}
                  onChange={(v) => setForm(f => ({ ...f, recurrence: v }))}
                  options={[
                    { value: 'none', label: 'Does not repeat' },
                    { value: 'daily', label: 'Daily' },
                    { value: 'weekly', label: 'Weekly' },
                    { value: 'biweekly', label: 'Bi-weekly' },
                    { value: 'monthly', label: 'Monthly' },
                  ]}
                  aria-label="Recurrence"
                />
              </div>
              {form.recurrence !== 'none' && (
                <DatePicker
                  label="Repeat Until"
                  value={form.recurrenceEndDate}
                  onChange={(v) => setForm(f => ({ ...f, recurrenceEndDate: v }))}
                />
              )}
              {/* Iter-18 CAL edit parity: Customer typeahead + Location + Assignees + Recurrence + Crew. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                {form.customerId ? (
                  <div className="flex items-center gap-2 px-3 py-2" style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                    <Chip>{form.customerName || 'Unnamed'}</Chip>
                    <button
                      type="button"
                      onClick={() => { setForm(f => ({ ...f, customerId: '', customerName: '' })); setCustomerSearch(''); }}
                      className="ml-auto text-gray-400 hover:text-gray-600"
                      aria-label="Clear customer"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="relative">
                    <input
                      type="text"
                      className="input"
                      placeholder="Search customers…"
                      value={customerSearch}
                      onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }}
                      onFocus={() => setShowCustomerDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 150)}
                    />
                    {showCustomerDropdown && customerSearch && filteredCustomers.length > 0 && (
                      <div className="absolute z-10 left-0 right-0 mt-1 bg-white border max-h-56 overflow-auto" style={{ borderRadius: 'var(--radius-md)', borderColor: 'var(--color-border)' }}>
                        {filteredCustomers.map(c => (
                          <button
                            key={c.id}
                            type="button"
                            className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setForm(f => ({ ...f, customerId: c.id, customerName: c.name || c.company_name || 'Unnamed' }));
                              setCustomerSearch('');
                              setShowCustomerDropdown(false);
                            }}
                          >
                            <div className="font-medium text-gray-900">{c.name || c.company_name || 'Unnamed'}</div>
                            {c.company_name && c.name && <div className="text-xs text-gray-500">{c.company_name}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <Input
                label="Location"
                value={form.location}
                onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))}
                placeholder="Address or location name"
              />
              {crews.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Crew</label>
                  <SelectMenu
                    width="full"
                    value={form.crewId}
                    placeholder="Unassigned"
                    onChange={(v) => setForm(f => ({ ...f, crewId: v }))}
                    options={[
                      { value: '', label: 'Unassigned' },
                      ...crews.map(c => ({ value: c.id, label: c.name })),
                    ]}
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assignees</label>
                <TypeaheadChips
                  value={form.assignees}
                  onChange={(arr) => setForm(f => ({ ...f, assignees: arr }))}
                  options={assigneeOptions}
                  placeholder="Add team members…"
                  aria-label="Assignees"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  className="input w-full min-h-[80px] resize-y"
                  rows={3}
                  value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                />
              </div>
            </>
          ) : (
            <>
              <div>
                {!isWO && <h4 className="text-base font-medium text-gray-900">{event.title}</h4>}
                {event.customerName && (
                  <p className="text-sm text-gray-500 mt-0.5">{event.customerName}</p>
                )}
              </div>
              {isWO ? (
                /* IDI-537: the WO's own range, same on every day of a span; never the 17:00 default. */
                <div className="text-sm">
                  <span className="text-gray-500">When:</span>{' '}
                  <span className="text-gray-900">
                    {formatWoRangeHouse({
                      start: event.date || event.start,
                      end: event.end,
                      startTime: event.startTime,
                      stopTime: event.stopTime,
                      endTime: event.endTime,
                    })}
                  </span>
                </div>
              ) : (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-500">Date:</span>{' '}
                  <span className="text-gray-900">
                    {/* IDI-609: a span reads "start → end"; one-day events unchanged. */}
                    {form.endDate && form.date && form.endDate > form.date ? `${form.date} \u2192 ${form.endDate}` : form.date}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Time:</span>{' '}
                  <span className="text-gray-900">{event.allDay ? 'All day' : `${form.startTime} – ${form.endTime}`}</span>
                </div>
              </div>
              )}
              {form.crewId && crews.length > 0 && (
                <div className="text-sm">
                  <span className="text-gray-500">Crew:</span>{' '}
                  <span className="text-gray-900">
                    {crews.find(c => c.id === form.crewId)?.name || form.crewId}
                  </span>
                </div>
              )}
              {event.location && (
                <div className="text-sm">
                  <span className="text-gray-500">Location:</span>{' '}
                  <span className="text-gray-900">{event.location}</span>
                </div>
              )}
              {event.notes && (
                <div className="text-sm">
                  <span className="text-gray-500">Notes:</span>{' '}
                  <span className="text-gray-900">{event.notes}</span>
                </div>
              )}
              {isImported && (
                <p className="text-xs text-gray-400 italic">
                  This event was imported and cannot be edited.
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-3">
            {canEdit && (
              <button
                onClick={handleDelete}
                className="text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Delete
              </button>
            )}
            {/* PT-40.4 (2026-07-27): 'View Work Order' link — rendered only
                when the modal is showing a WO event. Closes the modal, then
                navigates via react-router (preserves state.jobId for WO detail). */}
            {isWorkOrder && (
              <button
                type="button"
                onClick={handleViewWorkOrder}
                className="inline-flex items-center gap-1 text-sm font-medium"
                style={{ color: 'var(--color-action-primary)' }}
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                View Work Order
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {editing ? (
              <>
                <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} loading={saving}>
                  Save
                </Button>
              </>
            ) : (
              <Button variant="secondary" size="sm" onClick={onClose}>
                Close
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
